/**
 * Native Web Share API helpers (files + text) with clipboard/download fallbacks.
 * Android Capacitor: prefer text+url share sheet first (files often block the sheet).
 */

import { Capacitor } from "@capacitor/core";
import { openShareDialog } from "./shareDialog";

export type NativeShareResult = "shared" | "downloaded" | "copied";

/** Laptop/desktop (website or Windows software) — use the in-app share panel. */
function isDesktopLike(): boolean {
  if (isAndroidNative()) return false;
  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  return !/Android|iPhone|iPad|iPod|Mobile/i.test(ua);
}

function isAndroidNative(): boolean {
  try {
    return Capacitor.isNativePlatform() && Capacitor.getPlatform() === "android";
  } catch {
    return false;
  }
}

export async function shareNative(opts: {
  title: string;
  text: string;
  url?: string;
  file?: File | null;
  downloadName?: string;
}): Promise<NativeShareResult> {
  const { title, text, url, file, downloadName } = opts;

  const textData: ShareData = { title, text };
  if (url) textData.url = url;

  if (isDesktopLike()) {
    openShareDialog({ title, text, url, file, downloadName });
    return "shared";
  }

  // Android WebView: text/url share opens WhatsApp etc.; file share often fails → copy.
  if (isAndroidNative() && typeof navigator !== "undefined" && typeof navigator.share === "function") {
    try {
      await navigator.share(textData);
      if (file) downloadBlob(file, downloadName ?? file.name);
      return "shared";
    } catch (e) {
      if (e instanceof DOMException && e.name === "AbortError") throw e;
    }
  }

  if (file && typeof navigator !== "undefined" && navigator.share && navigator.canShare) {
    const data: ShareData = { title, text, files: [file] };
    if (url) data.url = url;
    try {
      if (navigator.canShare(data)) {
        await navigator.share(data);
        return "shared";
      }
    } catch (e) {
      if (e instanceof DOMException && e.name === "AbortError") throw e;
    }
  }

  try {
    if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
      await navigator.share(textData);
      if (file) downloadBlob(file, downloadName ?? file.name);
      return "shared";
    }
  } catch (e) {
    if (e instanceof DOMException && e.name === "AbortError") throw e;
  }

  if (file) downloadBlob(file, downloadName ?? file.name);
  const clip = url ? `${text}\n${url}` : text;
  try {
    await navigator.clipboard.writeText(clip);
  } catch {
    /* ignore */
  }
  return file ? "downloaded" : "copied";
}

export function downloadBlob(blob: Blob, filename: string): void {
  const href = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = href;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(href);
}

export async function canvasToPngFile(
  canvas: HTMLCanvasElement,
  filename: string,
): Promise<File | null> {
  const blob: Blob | null = await new Promise((resolve) =>
    canvas.toBlob((b) => resolve(b), "image/png"),
  );
  if (!blob) return null;
  return new File([blob], filename, { type: "image/png" });
}
